// Client-side half of the impersonation session hand-off. The admin
// console (AdminConsole.tsx) mints a token via POST
// /v1/admin/tenants/{slug}/impersonate and opens a new tab carrying it;
// ImpersonationEntry.tsx is that tab's landing page, which calls
// setImpersonationSession() once and then does a full page reload so the
// rest of the app (App.tsx, useCurrentUser.ts, api.ts) picks it up exactly
// the same way it would pick up a real MSAL login on first paint, rather
// than needing special-cased reactive re-rendering for a session that was
// just written.
//
// sessionStorage, not sessionStorage: the token is a 30-minute tenant-admin
// credential, so it lives only in the tab that received it and is gone when
// that tab closes, rather than sitting in storage every script on the origin
// and every other tab can read. The hand-off opens a new tab that writes its
// own copy, so nothing else needs it shared; another tab against the same
// tenant means minting another session. (The tenant-facing MSAL cache in
// lib/msal.ts is a separate decision and stays in sessionStorage.)

export interface ImpersonationSession {
  token: string
  tenantSlug: string
  adminEmail: string
  expiresAt: string // ISO timestamp
}

const STORAGE_KEY = 'quill_impersonation_session'

function isValidSession(value: unknown): value is ImpersonationSession {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v['token'] === 'string' && v['token'].length > 0 &&
    typeof v['tenantSlug'] === 'string' && v['tenantSlug'].length > 0 &&
    typeof v['adminEmail'] === 'string' && v['adminEmail'].length > 0 &&
    typeof v['expiresAt'] === 'string' && !Number.isNaN(Date.parse(v['expiresAt']))
  )
}

// Returns null for anything that isn't a live, well-formed session --
// missing, malformed (a future format this build doesn't recognize, or
// storage tampering), or expired. Expiry is checked against expiresAt, not
// re-derived from the token itself: the token is opaque to this file by
// design, exactly as it is to every other frontend consumer -- only the
// API ever verifies its signature.
export function getImpersonationSession(): ImpersonationSession | null {
  const raw = sessionStorage.getItem(STORAGE_KEY)
  if (!raw) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    sessionStorage.removeItem(STORAGE_KEY)
    return null
  }

  if (!isValidSession(parsed)) {
    sessionStorage.removeItem(STORAGE_KEY)
    return null
  }

  if (Date.parse(parsed.expiresAt) <= Date.now()) {
    sessionStorage.removeItem(STORAGE_KEY)
    return null
  }

  return parsed
}

export function setImpersonationSession(session: ImpersonationSession): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session))
}

export function clearImpersonationSession(): void {
  sessionStorage.removeItem(STORAGE_KEY)
}

// The hand-off payload travels in the URL fragment (`#...`), never the
// query string or path: a fragment is never sent to the server on
// navigation (only query/path are), so the token never reaches Azure
// Static Web Apps' own request logs the way a `?token=` would -- the same
// reasoning classic OAuth implicit-flow redirects relied on. Base64,
// not URL-encoded JSON directly: keeps the fragment free of characters
// that would otherwise need their own escaping on top of whatever the
// browser already does to fragments.
export function encodeImpersonationPayload(session: ImpersonationSession): string {
  return btoa(JSON.stringify(session))
}

export function decodeImpersonationPayload(fragment: string): ImpersonationSession | null {
  let parsed: unknown
  try {
    parsed = JSON.parse(atob(fragment))
  } catch {
    return null
  }
  return isValidSession(parsed) ? parsed : null
}
